import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LoaderService } from 'src/app/loader.service';
import { SharedService } from 'src/app/shared/shared.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';
import { AUTHZ_FEATURES } from 'src/app/constants/permissions';
import { AuthzAccessHintService } from 'src/app/shared/authz-access-hint/authz-access-hint.service';

const IDENTIFIERS = {
  CITY: 'CITY',
  SHIFT: 'SHIFT',
  SERVICE: 'SERVICE'
};

const AMOUNT_FIELDS = [
  {
    key: 'negativeBalanceWarn',
    label: 'Warning limit for negative balance',
    placeholder: '0',
    limitType: 'NEGATIVE_BALANCE_WARN',
    warning: true
  },
  {
    key: 'negativeBalanceBlock',
    label: 'Block limit for negative balance',
    placeholder: '0',
    limitType: 'NEGATIVE_BALANCE',
    warning: false
  },
  {
    key: 'negativeDueBalanceWarn',
    label: 'Warning limit for negative due balance',
    placeholder: '0',
    limitType: 'NEGATIVE_DUE_BALANCE_WARN',
    warning: true
  },
  {
    key: 'negativeDueBalanceBlock',
    label: 'Block limit for negative due balance',
    placeholder: '0',
    limitType: 'NEGATIVE_DUE_BALANCE',
    warning: false
  }
];

@Component({
  selector: 'app-limit-store-create',
  templateUrl: './limit-store-create.component.html',
  styleUrls: ['./limit-store-create.component.css']
})
export class LimitStoreCreateComponent implements OnInit, OnDestroy {
  public amountFields = AMOUNT_FIELDS;
  public limitForm = new FormGroup({
    city: new FormControl(null, [Validators.required]),
    shift: new FormControl(null),
    service: new FormControl(null),
    negativeBalanceWarn: new FormControl(null),
    negativeBalanceBlock: new FormControl(null),
    negativeDueBalanceWarn: new FormControl(null),
    negativeDueBalanceBlock: new FormControl(null),
    priority: new FormControl(1, [Validators.required]),
    active: new FormControl(true)
  });

  public cities: { id: string, name: string }[] = [];
  public shifts: string[] = [];
  public services: { id: string, name: string }[] = [];
  public submitting = false;
  public authzAccessHint: { message: string; requestUrl: string } | null = null;

  private subs: Subscription[] = [];

  constructor(
    private sharedService: SharedService,
    private loader: LoaderService,
    public toasterService: ToasterService,
    private authzAccessHintService: AuthzAccessHintService,
  ) {}

  ngOnInit() {
    this.loadAccessHint();
    this.fetchCities();
    this.subs.push(
      this.limitForm.get('city').valueChanges.subscribe(() => {
        this.limitForm.get('shift').setValue(null);
        this.limitForm.get('service').setValue(null);
        this.shifts = [];
        this.services = [];
        const city = this.limitForm.get('city').value;
        if (city) {
          this.fetchShifts(city.name);
          this.fetchServices(city.id);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  private async loadAccessHint(): Promise<void> {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData(AUTHZ_FEATURES.LIMIT_STORE);
  }

  private hasAmount(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  public fieldError(key: string): string | null {
    const raw = this.limitForm.get(key).value;
    if (!this.hasAmount(raw)) {
      return null;
    }
    const amount = Number(raw);
    if (!Number.isFinite(amount) || !Number.isInteger(amount) || amount >= 0) {
      return 'Positive values are not allowed. Enter a negative integer (e.g. -30).';
    }
    return null;
  }

  public get hasAmountError(): boolean {
    return this.amountFields.some((field) => !!this.fieldError(field.key));
  }

  public stopAmountScroll(event: WheelEvent) {
    const target = event.target as HTMLInputElement;
    if (target && target.tagName === 'INPUT' && target.type === 'number') {
      target.blur();
    }
  }

  public blockNonIntegerKey(event: KeyboardEvent) {
    const blocked = ['e', 'E', '+', '.', ','];
    if (blocked.indexOf(event.key) !== -1) {
      event.preventDefault();
    }
  }

  public blockNonIntegerPaste(event: ClipboardEvent) {
    const pasted = event.clipboardData && event.clipboardData.getData('text');
    if (!pasted || !/^-?[0-9]+$/.test(pasted.trim())) {
      event.preventDefault();
    }
  }

  private fetchCities() {
    this.loader.openLoading();
    this.sharedService.fetchCities().toPromise()
      .then((res: any) => {
        this.cities = ((res && res.data && res.data.cities) || [])
          .filter((city: any) => city.active !== false)
          .map((city: any) => ({ id: city._id, name: city.displayName }));
      })
      .catch(() => {
        this.showWarning('Error fetching cities');
      })
      .finally(() => this.loader.closeLoading());
  }

  private fetchShifts(cityDisplayName: string) {
    if (!cityDisplayName) { return; }
    this.sharedService.fetchShifts(cityDisplayName).toPromise()
      .then((res: any) => {
        const data: string[] = Array.isArray(res) ? res : (res && res.data ? res.data : []);
        this.shifts = (data || []).filter(Boolean);
      })
      .catch(() => {
        this.shifts = [];
        this.showWarning('Error fetching shifts');
      });
  }

  private fetchServices(cityId: string) {
    if (!cityId) { return; }
    this.sharedService.cityService(cityId).toPromise()
      .then((res: any) => {
        const mapped = ((res && res.data) || [])
          .filter((sd: any) => sd && sd.active !== false && sd.service)
          .map((sd: any) => ({
            id: sd.service._id,
            name: sd.service.name
          }));
        const seen = {};
        this.services = mapped.filter((svc) => {
          if (!svc.id || seen[svc.id]) {
            return false;
          }
          seen[svc.id] = true;
          return true;
        });
      })
      .catch(() => {
        this.services = [];
        this.showWarning('Error fetching services');
      });
  }

  public createLimit() {
    const validationError = this.validateForm();
    if (validationError) {
      this.showWarning(validationError);
      return;
    }

    const payloads = this.buildPayloads();
    this.submitting = true;
    this.loader.openLoading();

    this.createSequentially(payloads, 0, { pending: 0, saved: 0 })
      .then((counts) => {
        this.showSuccess(this.successMessage(counts));
        this.resetAmounts();
      })
      .catch((error) => {
        this.showWarning(this.readError(error));
      })
      .finally(() => {
        this.submitting = false;
        this.loader.closeLoading();
      });
  }

  private createSequentially(
    payloads: any[],
    index: number,
    counts: { pending: number, saved: number }
  ): Promise<{ pending: number, saved: number }> {
    if (index >= payloads.length) {
      return Promise.resolve(counts);
    }
    return this.sharedService.createLimit(payloads[index]).toPromise()
      .then((res: any) => {
        const outcome = this.readCreateOutcome(res);
        if (outcome === 'PENDING_APPROVAL') {
          counts.pending += 1;
        } else if (outcome === 'SAVED') {
          counts.saved += 1;
        }
        return this.createSequentially(payloads, index + 1, counts);
      });
  }

  private readCreateOutcome(res: any): 'PENDING_APPROVAL' | 'SAVED' | 'UNKNOWN' {
    const data = res && res.data;
    if (data && data.outcome) {
      return data.outcome;
    }
    const payload = data && data.data;
    if (payload && payload.status === 'PENDING_APPROVAL') {
      return 'PENDING_APPROVAL';
    }
    if (payload && payload.limitType) {
      return 'SAVED';
    }
    return 'UNKNOWN';
  }

  private successMessage(counts: { pending: number, saved: number }): string {
    const { pending, saved } = counts;
    if (saved > 0 && pending === 0) {
      return saved === 1
        ? 'Limit saved. It is visible in the list.'
        : `${saved} limits saved. They are visible in the list.`;
    }
    if (pending > 0 && saved === 0) {
      return pending === 1
        ? 'Submitted for checker approval. The limit appears in the list after approval.'
        : `${pending} limits submitted for checker approval. They appear in the list after approval.`;
    }
    if (pending > 0 && saved > 0) {
      return `${saved} limit(s) saved immediately; ${pending} pending checker approval.`;
    }
    return 'Limits submitted successfully.';
  }

  private resolveIdentifiers(): { identifier: string, identifierId: string }[] | { error: string } {
    const city = this.limitForm.get('city').value;
    const shift = this.limitForm.get('shift').value;
    const service = this.limitForm.get('service').value;
    if (!city) {
      return { error: 'City is required' };
    }
    const scopes: { identifier: string, identifierId: string }[] = [];
    if (shift) {
      scopes.push({ identifier: IDENTIFIERS.SHIFT, identifierId: shift });
    }
    if (service) {
      scopes.push({ identifier: IDENTIFIERS.SERVICE, identifierId: service.id });
    }
    if (scopes.length === 0) {
      scopes.push({ identifier: IDENTIFIERS.CITY, identifierId: city.id });
    }
    return scopes;
  }

  private validateForm(): string | null {
    const resolved = this.resolveIdentifiers();
    if ((resolved as { error?: string }).error) {
      return (resolved as { error: string }).error;
    }

    const filled = this.amountFields.filter((field) => this.hasAmount(this.limitForm.get(field.key).value));
    if (!filled.length) {
      return 'Enter at least one limit';
    }
    const invalid = filled.find((field) => this.fieldError(field.key) !== null);
    if (invalid) {
      return this.fieldError(invalid.key);
    }

    const priority = Number(this.limitForm.get('priority').value);
    if (!Number.isFinite(priority) || priority < 1) {
      return 'Priority must be a positive number';
    }
    return null;
  }

  private buildPayloads(): any[] {
    const city = this.limitForm.get('city').value;
    const scopes = this.resolveIdentifiers() as { identifier: string, identifierId: string }[];
    const priority = Number(this.limitForm.get('priority').value);
    const active = !!this.limitForm.get('active').value;
    const payloads = [];
    scopes.forEach((scope) => {
      this.amountFields.forEach((field) => {
        const raw = this.limitForm.get(field.key).value;
        if (!this.hasAmount(raw)) {
          return;
        }
        const amount = Number(raw);
        payloads.push({
          identifier: scope.identifier,
          identifierId: scope.identifierId,
          city: city.id,
          limitType: field.limitType,
          limit: amount,
          priority,
          active,
          metadata: {
            negativeBalanceWarn: field.warning ? amount : undefined,
            minRechargeAmountRanges: [],
            negBalanceSelectedServicesAllowed: false
          }
        });
      });
    });
    return payloads;
  }

  private resetAmounts() {
    this.amountFields.forEach((field) => {
      this.limitForm.get(field.key).setValue(null, { emitEvent: false });
    });
  }

  private readError(error: any): string {
    const body = error && error.error;
    const nested = body && body.data && body.data.error;
    return (body && body.message)
      || (nested && nested.message)
      || (error && error.message)
      || 'Could not submit limit';
  }

  private showWarning(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message: message }));
  }

  private showSuccess(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.SUCCESS, message: message }));
  }
}
